"use client";
import { AvailableSpinsCard } from "@/components/AvailableSpinsCard/AvailableSpinsCard";
import { PrizesWonCard } from "@/components/PrizesWonCard/PrizesWonCard";
import { useQuestCompletionsV2 } from "@/components/QuestsV2/useQuestCompletions";
import { StatsCard } from "@/components/StatsCard/StatsCard";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button as ShButton } from "@/components/ui/button";
import { FrostBorderCard } from "@/components/ui/custom/FrostBorderCard";
import { Instructions } from "@/components/ui/custom/prize-wheel/Instructions";
import PrizeWheelModal from "@/components/ui/custom/prize-wheel/PrizeWheelModal";
import { WonPrizesSnapshot } from "@/components/ui/custom/prize-wheel/WonPrizesSnapshot";
import {
  PROMOTIONAL_PRIZES_STORAGE_KEY,
  PROMOTIONAL_WALLETS,
} from "@/constants";
import { usePassportProvider } from "@/context";
import { useLoginModal } from "@/context/LoginModalContext";
import { useAccessTokenState } from "@/hooks";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { authorizedGet, authorizedPost } from "@/utils/request";
import { trackEvent } from "@analytics";
import { Card, CardContent } from "@mui/material";
import { Copy, Gift } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import Analytics from "../../_components/analytics";
import {
  type ReferralCode,
  type ReferralStats,
  getUserReferralCode,
  getUserReferralStats,
} from "../../_lib/apis/referral";

export default function ReferralPage() {
  const { passportState, walletAddress } = usePassportProvider();
  const { openLoginModal } = useLoginModal();
  const accessToken = useAccessTokenState();
  const { isLoggedIn } = usePassportProvider();
  const [copiedPrimary, setCopiedPrimary] = useState(false);
  const [copiedSpecial, setCopiedSpecial] = useState(false);
  const [wheelOpen, setWheelOpen] = useState(false);
  const [isClaimingSpin, setIsClaimingSpin] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [localStorageUpdateTrigger, setLocalStorageUpdateTrigger] = useState(0);

  // Types for localStorage prizes
  interface StoredPrize {
    amount: number;
    name: string;
    type: string;
    category: string;
  }

  const convertPromotionalPrizes = useCallback((rawPrizes: StoredPrize[]) => {
    return rawPrizes.map((prize: StoredPrize) => {
      // Map categories back to proper names and types
      let name = prize.name;
      let type = prize.type;

      if (prize.category === "ps5") {
        name = "PlayStation 5";
        type = "physical";
      } else if (prize.category === "xbox") {
        name = "Series X";
        type = "physical";
      } else if (prize.category === "meta-quest") {
        name = "Meta Quest 3";
        type = "physical";
      } else if (prize.category === "switch2") {
        name = "Switch 2";
        type = "physical";
      } else if (prize.category === "gift-cards") {
        name = "Gift Cards";
        type = "physical";
      } else if (prize.category === "gems") {
        name = "Gems";
        type = "gems";
      } else if (prize.category === "imx") {
        name = "IMX";
        type = "imx";
      }

      return {
        amount: prize.amount,
        name: name,
        type: type,
      };
    });
  }, []);

  // TODO: Clean up after promotion
  const isPromotionalWallet = useCallback((address: string | undefined) => {
    if (!address) return false;
    return PROMOTIONAL_WALLETS.some(
      (addr) => addr.toLowerCase() === address.toLowerCase(),
    );
  }, []);

  const getPromotionalPrizes = useCallback(
    (address: string) => {
      try {
        const stored = localStorage.getItem(PROMOTIONAL_PRIZES_STORAGE_KEY);
        if (!stored) return [];
        const data = JSON.parse(stored);
        const rawPrizes: StoredPrize[] = data[address.toLowerCase()] || [];

        // Convert category-based prizes back to individual prize format for PrizesWonCard
        const convertedPrizes = convertPromotionalPrizes(rawPrizes);

        return convertedPrizes;
      } catch {
        return [];
      }
    },
    [convertPromotionalPrizes],
  );

  // TODO: Clean up after promotion
  const refreshLocalStorageData = useCallback(() => {
    setLocalStorageUpdateTrigger((prev) => prev + 1);
  }, []);
  const copiedPrimaryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const copiedSpecialTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const { referralSept8 } = useFeatureFlags();

  const { data: spinsData, mutate: refreshSpins } = useSWR<{
    availableSpins?: number;
  }>(accessToken && isLoggedIn ? ["available-spins", accessToken] : null, () =>
    authorizedGet<{ availableSpins?: number }>(
      "/v2/rewards/spin/available-spins",
      accessToken as string,
    ),
  );

  const spinsAvailable = spinsData?.availableSpins || 0;

  const handleWheelModalClose = (open: boolean) => {
    setWheelOpen(open);
    if (!open && accessToken && isLoggedIn) {
      // Refresh both spins and prizes data after successful spin
      refreshSpins();
      refreshPrizes();
    }
  };

  //SWR hook for prizes won data with proper caching and error handling
  const {
    data: prizesData,
    error: prizesError,
    isLoading: prizesLoading,
    mutate: refreshPrizes,
  } = useSWR<{
    result?: Array<{
      amount: number;
      name: string;
      type: string;
    }>;
  }>(
    accessToken && isLoggedIn ? ["prizes", accessToken] : null,
    async () => {
      try {
        return await authorizedGet<{
          result?: Array<{
            amount: number;
            name: string;
            type: string;
          }>;
        }>("/v2/rewards/spin/prizes-won", accessToken as string);
      } catch (error: unknown) {
        // Handle 429 rate limiting with exponential backoff
        if (
          error &&
          typeof error === "object" &&
          "status" in error &&
          error.status === 429
        ) {
          // Wait 1 second before retrying
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return await authorizedGet<{
            result?: Array<{
              amount: number;
              name: string;
              type: string;
            }>;
          }>("/v2/rewards/spin/prizes-won", accessToken as string);
        }
        throw error;
      }
    },
    {
      // Add retry configuration
      errorRetryCount: 3,
      errorRetryInterval: 1000,
      // Deduplicate requests
      dedupingInterval: 5000,
      // Don't revalidate on focus to avoid unnecessary requests
      revalidateOnFocus: false,
      // Don't revalidate on reconnect to avoid unnecessary requests
      revalidateOnReconnect: false,
    },
  );

  // Derived value from SWR data - merge with promotional prizes if applicable
  // biome-ignore lint/correctness/useExhaustiveDependencies: <Need to trigger reload on localStorage update>
  const prizesWon = useMemo(() => {
    const apiPrizes = prizesData?.result || [];

    // TODO: Clean up after promotion
    // If this is a promotional wallet, merge localStorage prizes with API prizes
    if (walletAddress && isPromotionalWallet(walletAddress)) {
      const stored = localStorage.getItem(PROMOTIONAL_PRIZES_STORAGE_KEY);
      if (!stored) return [];
      const data = JSON.parse(stored);
      const rawPrizes: StoredPrize[] = data[walletAddress.toLowerCase()] || [];

      // Convert category-based prizes back to individual prize format for PrizesWonCard
      const convertedPrizes = convertPromotionalPrizes(rawPrizes);

      return [...apiPrizes, ...convertedPrizes];
    }

    return apiPrizes;
  }, [
    prizesData?.result,
    walletAddress,
    isPromotionalWallet,
    getPromotionalPrizes,
    localStorageUpdateTrigger,
  ]);

  const { data, error, isLoading } = useSWR<ReferralCode>(
    accessToken ? ["referral-code", accessToken] : null,
    () => getUserReferralCode(accessToken as string),
  );

  const { data: stats } = useSWR<ReferralStats>(
    accessToken ? ["referral-stats", accessToken] : null,
    () => getUserReferralStats(accessToken as string),
  );

  const { completedQuests, mutate: refreshQuestCompletions } =
    useQuestCompletionsV2();

  // Check if the free spin quest is completed
  const isFreeSpinClaimed = useMemo(() => {
    if (!completedQuests) return false;
    return Object.values(completedQuests).some(
      (quest) =>
        quest.external_id === "free-spin" &&
        quest.game_id === "11111111-1111-1111-1111-111111111111",
    );
  }, [completedQuests]);

  const utmParams = {
    utm_source: "referral",
  };

  const SHARE_LINK_TEXT = "Use my Referral Race Code. Earn a Special Reward!";

  const referralLink = data?.referral_code
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/referral/share/${data.referral_code}?${new URLSearchParams(utmParams).toString()}`
    : "";

  const handleShareLink = async (origin: "primary" | "special") => {
    try {
      await navigator.clipboard.writeText(referralLink);
      if (origin === "primary") {
        trackEvent({
          component: "Button",
          action: "Pressed",
          extras: {
            control: "SharePrimaryReferralCode",
            section: "ReferralPage",
            userJourney: "Referral",
          },
        });
        setCopiedPrimary(true);
        if (copiedPrimaryTimeoutRef.current) {
          clearTimeout(copiedPrimaryTimeoutRef.current);
        }
        copiedPrimaryTimeoutRef.current = setTimeout(() => {
          setCopiedPrimary(false);
        }, 2000);
      } else {
        trackEvent({
          component: "Button",
          action: "Pressed",
          extras: {
            control: "ShareSpecialReferralCode",
            section: "ReferralPage",
            userJourney: "Referral",
          },
        });
        setCopiedSpecial(true);
        if (copiedSpecialTimeoutRef.current) {
          clearTimeout(copiedSpecialTimeoutRef.current);
        }
        copiedSpecialTimeoutRef.current = setTimeout(() => {
          setCopiedSpecial(false);
        }, 2000);
      }

      // Share behavior: mobile uses Web Share API, desktop opens X (Twitter) intent
      const isMobile =
        typeof window !== "undefined" &&
        window.matchMedia("(max-width: 767px)").matches;
      const hasWebShare =
        typeof navigator !== "undefined" &&
        typeof (
          navigator as Navigator & {
            share?: (data: ShareData) => Promise<void>;
          }
        ).share === "function";

      if (isMobile && hasWebShare && referralLink) {
        try {
          await (
            navigator as Navigator & {
              share: (data: ShareData) => Promise<void>;
            }
          ).share({
            title: SHARE_LINK_TEXT,
            url: referralLink,
          });
        } catch {
          // Ignore cancel or failure silently
        }
      } else if (!isMobile && referralLink) {
        const tweetUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(SHARE_LINK_TEXT)}`;
        window.open(tweetUrl, "_blank", "noopener,noreferrer");
      }
    } catch (err) {}
  };

  const handleSignIn = () => {
    trackEvent({
      component: "Button",
      action: "Pressed",
      extras: {
        control: "SignIn",
        section: "ReferralPage",
        userJourney: "Referral",
      },
    });
    openLoginModal();
  };

  const handleCopyOnly = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      trackEvent({
        component: "Button",
        action: "Pressed",
        extras: {
          control: "CopyReferralCode",
          section: "ReferralPage",
          userJourney: "Referral",
        },
      });
      setCopiedPrimary(true);
      if (copiedPrimaryTimeoutRef.current) {
        clearTimeout(copiedPrimaryTimeoutRef.current);
      }
      copiedPrimaryTimeoutRef.current = setTimeout(() => {
        setCopiedPrimary(false);
      }, 2000);
    } catch (err) {}
  };

  useEffect(() => {
    return () => {
      if (copiedPrimaryTimeoutRef.current) {
        clearTimeout(copiedPrimaryTimeoutRef.current);
      }
      if (copiedSpecialTimeoutRef.current) {
        clearTimeout(copiedSpecialTimeoutRef.current);
      }
    };
  }, []);

  const handleShareX = () => {
    if (!referralLink) return;
    const tweetUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(SHARE_LINK_TEXT)}`;
    trackEvent({
      component: "Button",
      action: "Pressed",
      extras: {
        control: "ShareXReferralCode",
        section: "ReferralPage",
        userJourney: "Referral",
      },
    });
    window.open(tweetUrl, "_blank", "noopener,noreferrer");
  };

  const handleShareFacebook = () => {
    if (!referralLink) return;
    const fbUrl = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(referralLink)}&redirect_uri=${encodeURIComponent(
      typeof window !== "undefined"
        ? window.location.origin
        : "https://play.immutable.com",
    )}`;
    trackEvent({
      component: "Button",
      action: "Pressed",
      extras: {
        control: "ShareFacebookReferralCode",
        section: "ReferralPage",
        userJourney: "Referral",
      },
    });
    window.open(fbUrl, "_blank", "noopener,noreferrer");
  };

  const handleClaimFreeSpin = async () => {
    if (!accessToken || !isLoggedIn) {
      openLoginModal();
      return;
    }

    setIsClaimingSpin(true);
    setClaimError(null);

    try {
      trackEvent({
        component: "Button",
        action: "Pressed",
        extras: {
          control: "ClaimFreeSpin",
          section: "ReferralPage",
          userJourney: "Referral",
        },
      });

      const response = await authorizedPost(
        "/v1/rewards/claim",
        {
          event: "11111111-1111-1111-1111-111111111111-free-spin-completed",
        },
        accessToken,
      );

      if (response.claim_status === "already_claimed") {
        setClaimError("You have already claimed your free spin!");
        return;
      }

      // Success - refresh spins data and quest completions
      refreshSpins();
      refreshQuestCompletions();

      trackEvent({
        component: "Button",
        action: "Succeeded",
        extras: {
          control: "ClaimFreeSpin",
          section: "ReferralPage",
          userJourney: "Referral",
        },
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to claim free spin";
      setClaimError(errorMessage);

      trackEvent({
        component: "Button",
        action: "Failed",
        extras: {
          control: "ClaimFreeSpin",
          section: "ReferralPage",
          userJourney: "Referral",
          error: errorMessage,
        },
      });
    } finally {
      setIsClaimingSpin(false);
    }
  };

  // Deterministic UTC day key used to seed the RNG so the list
  // changes once per UTC day but remains stable within that day.
  const utcDayKey = useMemo(() => {
    const now = new Date();
    const y = now.getUTCFullYear();
    const m = String(now.getUTCMonth() + 1).padStart(2, "0");
    const d = String(now.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, []);

  // Build 10 entries deterministically from UTC day seed.
  const leaderboardEntries: Array<{ wallet: string; stars: number }> =
    useMemo(() => {
      const seedNum = Number(utcDayKey.replaceAll("-", ""));
      const starsSequence = [10, 9, 9, 8, 7, 6, 6, 6, 5, 5];

      // Inline PRNG + wallet generator to avoid extra hook deps
      const mulberry32Local = (seed: number) => {
        let state = seed >>> 0;
        return () => {
          state = (state + 0x6d2b79f5) >>> 0;
          let t = state;
          t = Math.imul(t ^ (t >>> 15), t | 1);
          t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
          return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
        };
      };

      const rng = mulberry32Local(seedNum || 1);
      const generateWalletLocal = (rand: () => number): string => {
        const hex = "0123456789abcdef";
        const pick = (n: number) =>
          Array.from({ length: n }, () => hex[Math.floor(rand() * 16)]).join(
            "",
          );
        const start = pick(4);
        const end = pick(4);
        return `0x${start}...${end}`;
      };

      return Array.from({ length: 10 }, (_, idx) => ({
        wallet: generateWalletLocal(rng),
        stars: starsSequence[idx] ?? 1,
      }));
    }, [utcDayKey]);

  const isAuthenticated = passportState.ready && passportState.authenticated;

  const renderStars = (stars: number, keyPrefix: string) => {
    const capped = Math.min(stars, 10);
    const starKeys = Array.from(
      { length: capped },
      (_, i) => `${keyPrefix}-star-${capped}-${i}`,
    );
    return (
      <div className="grid grid-cols-5 md:grid-cols-10 gap-1 [direction:rtl]">
        {starKeys.map((key) => (
          <img
            key={key}
            src="/assets/referral/star.gif"
            alt="star"
            className="h-4 w-4 block"
          />
        ))}
      </div>
    );
  };

  const LegendaryPrizesCard = () => {
    return (
      <Card variant="outlined" sx={{ width: "100%", height: "100%" }}>
        <CardContent sx={{ p: 3, height: "100%" }}>
          <div className="flex items-center h-full gap-4">
            <div className="relative flex items-center justify-center w-12 h-12 rounded-[4px] bg-gradient-to-br from-[#f191fa] to-[#83e3f0] overflow-hidden">
              <img
                src="/assets/referral/star.gif"
                alt="Star"
                className="w-6 h-6"
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 border-[8px] border-white/10"
                style={{ boxSizing: "border-box" }}
              />
            </div>
            <div className="min-w-0">
              <div className="bg-gradient-to-b from-[#f191fa] to-[#83e3f0] bg-clip-text text-transparent">
                <div className="text-[14px] leading-[20px]">
                  Legendary Prizes
                </div>
                <div className="text-[12px] leading-[16px]">Coming Soon</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const ReferralLeaderboard = ({
    scrollable = false,
  }: { scrollable?: boolean }) => {
    return (
      <div className={scrollable ? "flex flex-col h-full" : ""}>
        <h3 className="text-lg md:text-xl font-semibold mb-2 text-center md:text-left">
          Referral Leaderboard
        </h3>
        <p className="hidden md:block text-white/70 mb-4">Top 10</p>

        <div
          className={
            scrollable ? "flex-1 overflow-auto pr-2 space-y-0.5" : "space-y-0.5"
          }
        >
          {leaderboardEntries.map((entry, idx, arr) => {
            const isLast = idx === arr.length - 1;
            return (
              <div
                key={`row-${entry.wallet}`}
                className={`flex items-center rounded-[8px] p-3 mb-0.5 min-h-[60px] ${isLast ? "mb-0" : ""}`}
              >
                <span className="w-10 md:w-14 text-left font-mono">
                  {idx + 1}
                </span>
                <div className="mr-2 md:mr-4">
                  {idx + 1 <= 5 ? (
                    <div className="relative flex items-center justify-center w-10 h-10">
                      <Avatar className="h-10 w-10">
                        <AvatarImage
                          src="/assets/referral/Avatar.webp"
                          alt="avatar"
                        />
                      </Avatar>
                      <img
                        src={`/assets/referral/border${idx + 1}.webp`}
                        alt="border"
                        width={50}
                        height={50}
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 scale-150 pointer-events-none"
                      />
                    </div>
                  ) : (
                    <Avatar className="h-10 w-10">
                      <AvatarImage
                        src="/assets/referral/Avatar.webp"
                        alt="avatar"
                      />
                      <AvatarFallback>AV</AvatarFallback>
                    </Avatar>
                  )}
                </div>
                <div className="flex-1 text-sm font-mono tracking-tight">
                  {entry.wallet}
                </div>
                <div className="ml-auto flex justify-end">
                  {renderStars(entry.stars, `row-${entry.wallet}`)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full p-0">
      <Analytics screen="Referral" userJourney="Referral" />
      <div className="relative w-full md:min-h-[500px] flex items-end mb-8 overflow-hidden rounded-2xl bg-[#070707]">
        <div className="absolute inset-0 z-0 hidden md:flex items-end justify-end overflow-hidden">
          <video
            src="/video/prize-wheel/wheel-bg.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="h-full w-auto object-contain object-right-bottom"
          />
        </div>
        <div className="relative w-full md:w-[70%] px-4 md:px-6 pb-8 flex flex-col gap-4">
          <div className="pt-0">
            <h2 className="text-2xl md:text-4xl font-bold max-w-full md:max-w-[70%]">
              Refer & Play to Earn Legendary <br /> Spins of the $5,000 Prize
              Wheel
            </h2>
          </div>

          <p className="text-sm md:text-base text-white/70 max-w-full md:max-w-[50%]">
            Top Weekly Referrers Earn BONUS spins. <br /> Refer More. Quest
            More. Spin More.
          </p>

          {error && isAuthenticated && (
            <p className="text-red-400">
              Failed to load referral code. Please try again.
            </p>
          )}

          {!isAuthenticated ? (
            <div>
              <ShButton onClick={handleSignIn} className="min-w-[180px]">
                Sign in to get referral link
              </ShButton>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row gap-5 items-stretch md:items-center max-w-[600px]">
              <div className="flex flex-col gap-1">
                <span className="text-xs md:text-sm text-white/80 font-bold mb-1">
                  Refer a friend:
                </span>
                <ShButton
                  onClick={() => handleShareLink("primary")}
                  disabled={isLoading || !referralLink}
                  className="shrink-0 min-w-[140px] md:hidden"
                >
                  {copiedPrimary ? "Copied!" : "Share Referral Code"}
                </ShButton>
                <div className="hidden md:flex items-center gap-3">
                  <ShButton
                    onClick={handleCopyOnly}
                    className="min-w-[140px]"
                    leftIcon={<Copy className="h-4 w-4" />}
                  >
                    {copiedPrimary ? "Copied!" : "Copy Referral Code"}
                  </ShButton>
                  <ShButton
                    onClick={handleShareX}
                    size="icon"
                    aria-label="Share on X"
                    title="Share on X"
                    leftIcon={
                      <img
                        src="/icons/x.svg"
                        alt="X"
                        width={16}
                        height={16}
                        className="h-4 w-4"
                      />
                    }
                  />
                  <ShButton
                    onClick={handleShareFacebook}
                    size="icon"
                    aria-label="Share on Facebook"
                    title="Share on Facebook"
                    leftIcon={
                      <img
                        src="/icons/facebook.svg"
                        alt="Facebook"
                        width={16}
                        height={16}
                        className="h-4 w-4"
                      />
                    }
                  />
                </div>
              </div>
            </div>
          )}
          <p className="text-sm md:text-base text-white/70 max-w-full md:max-w-[50%]">
            Share your link. Your friend gets a free Weekly Draw entry. If they
            unlock an Uncommon Key or higher, you both earn 300 Gems + Special
            Prize Points.
          </p>

          {/* Claim Free Spin Section */}
          {isLoggedIn && !isFreeSpinClaimed && (
            <div className="mt-6 flex flex-col gap-3">
              <div className="flex flex-col gap-3">
                <ShButton
                  onClick={handleClaimFreeSpin}
                  disabled={isClaimingSpin}
                  className="shrink-0 min-w-[160px] md:max-w-[180px] text-black hover:opacity-90 focus-visible:ring-4 focus-visible:ring-black/20 [background-image:linear-gradient(90deg,color-mix(in_srgb,var(--gradient-to),white_18%),color-mix(in_srgb,var(--accent-5),white_18%),color-mix(in_srgb,var(--gradient-from),white_18%))]"
                  leftIcon={<Gift className="h-4 w-4" />}
                >
                  {isClaimingSpin ? "Claiming..." : "Claim Free Spin"}
                </ShButton>
                <p className="text-sm md:text-base text-white/70 max-w-full md:max-w-[50%]">
                  Claim your first one-time free spin on the prize wheel!
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoggedIn && (
        <div className="mb-8 flex flex-col lg:flex-row justify-between gap-2 lg:gap-2 items-stretch">
          <div className="flex-1 min-w-[200px]">
            <AvailableSpinsCard
              spinsAvailable={spinsAvailable}
              onOpenPrizeWheel={() => setWheelOpen(true)}
            />
          </div>
          <div className="flex-1 min-w-[300px]">
            <PrizesWonCard prizes={prizesWon} isLoading={prizesLoading} />
          </div>
          <div className="flex-1 min-w-[260px]">
            <StatsCard
              icon="People"
              title="Total Referrals"
              value={stats?.total_accepted_referrals || 0}
            />
          </div>
        </div>
      )}

      {/* Instructions Section */}
      <div className="mb-10 px-0 md:px-10">
        <Instructions />
      </div>

      <div className="mb-10 grid grid-cols-1 lg:grid-cols-2 gap-2 items-stretch">
        <FrostBorderCard
          className="order-2 lg:order-1 px-6 md:px-10 py-8 md:py-10"
          minHeight={720}
        >
          <ReferralLeaderboard />
        </FrostBorderCard>
        <div className="col-span-1 h-full order-1 lg:order-2">
          <WonPrizesSnapshot />
        </div>
      </div>

      <PrizeWheelModal
        open={wheelOpen}
        onOpenChange={handleWheelModalClose}
        spinsAvailable={spinsAvailable}
        onPrizeWon={refreshLocalStorageData}
      />
    </div>
  );
}
